import { motion } from "framer-motion";
import { Rocket, Target, Users, ShieldCheck } from "lucide-react";

const pillars = [
  { icon: Rocket, title: "Speed", copy: "Funding as fast as 24 hours — because timing is everything." },
  { icon: Target, title: "Precision", copy: "We match capital to your real business, not a rigid box." },
  { icon: Users, title: "Partnership", copy: "Real humans, real answers. No call-center runaround." },
  { icon: ShieldCheck, title: "Trust", copy: "No hidden fees, no gotchas. Transparent from day one." },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 md:py-32 bg-gradient-to-br from-[#0b1121] via-[#1e1b4b] to-[#312e81] text-white relative overflow-hidden"
    >
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-purple-600/30 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-fuchsia-600/20 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-purple-300">
            About Rocket Cap
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black mt-3 leading-[1.05]">
            Fueling the next generation of{" "}
            <span className="bg-gradient-to-r from-purple-300 to-sky-300 bg-clip-text text-transparent">
              American businesses.
            </span>
          </h2>
          <p className="mt-6 text-lg text-white/70 leading-relaxed">
            Rocket Cap Funding was built by operators and financiers who were
            tired of watching great businesses get stuck waiting on outdated
            banks. Today, we deploy capital to entrepreneurs across every state
            — quickly, transparently, and on terms that make sense for the real
            world.
          </p>
          <p className="mt-4 text-lg text-white/70 leading-relaxed">
            From family-run restaurants to fast-scaling e-commerce brands, our
            funding has helped 10,000+ businesses hit their next milestone.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-6 max-w-lg">
            <div>
              <div className="font-display text-3xl font-black text-white">$500M+</div>
              <div className="text-xs text-white/50 mt-1 uppercase tracking-widest">Deployed</div>
            </div>
            <div>
              <div className="font-display text-3xl font-black text-white">10K+</div>
              <div className="text-xs text-white/50 mt-1 uppercase tracking-widest">Businesses</div>
            </div>
            <div>
              <div className="font-display text-3xl font-black text-white">50</div>
              <div className="text-xs text-white/50 mt-1 uppercase tracking-widest">States served</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="grid grid-cols-2 gap-4"
        >
          {pillars.map((p) => (
            <div
              key={p.title}
              className="rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 p-6 hover:bg-white/10 hover:-translate-y-1 transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-500 grid place-items-center mb-4 shadow-lg shadow-purple-500/40">
                <p.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-display text-xl font-bold">{p.title}</h3>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">{p.copy}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
